import { useState } from 'react'
import dynamic from 'next/dynamic'
import classes from './ProductDetailsList.module.scss'
import { PortableText } from '@portabletext/react'
import Expand from 'react-expand-animated'

// We keep these as direct imports (always used for expand/collapse)
import IconPlus from '@/svgs/plus.svg';
import IconMinus from '@/svgs/minus.svg';

// Dynamically import icons that might only be used occasionally
const IconProductDynamic = dynamic(() => import('@/svgs/product.svg'))
const IconCulinaryDynamic = dynamic(() => import('@/svgs/culinary.svg'))
const IconNutritionDynamic = dynamic(() => import('@/svgs/nutrition.svg'))
const IconShippingDynamic = dynamic(() => import('@/svgs/currentlyShipping.svg'))
const IconTransparencyDynamic = dynamic(() => import('@/svgs/transparency.svg'))
const IconIngredientsDynamic = dynamic(() => import('@/svgs/Ingredients.svg'))
const IconSustainabilityDynamic = dynamic(() => import('@/svgs/sustainability.svg'))
const IconSeafoodQualityDynamic = dynamic(() => import('@/svgs/seafoodQuality.svg'))
const IconMemberPerksDynamic = dynamic(() => import('@/svgs/memberPerks.svg'))
const IconFlexibilityDynamic = dynamic(() => import('@/svgs/Flexibility.svg'))
const IconCurrentlyShippingDynamic = dynamic(() => import('@/svgs/ShippingDetail.svg'))

/**
 * Dynamically returns the correct icon component depending on the title.
 */
const getIconForTitle = (title) => {
  if (title?.includes('description'))
    return <IconProductDynamic className={classes['icon-details']} />
  if (title?.includes('cooking'))
    return <IconCulinaryDynamic className={classes['icon-details']} />
  if (title?.includes('nutritional'))
    return <IconNutritionDynamic className={classes['icon-details']} />
  if (title?.includes('sourcing') || title?.includes('Traceability'))
    return <IconTransparencyDynamic className={classes['icon-details']} />
  if (title?.includes('packaging'))
    return <IconShippingDynamic className={classes['icon-details']} />
  if (title?.includes('ingredients'))
    return <IconIngredientsDynamic className={classes['icon-details']} />
  if (title?.includes('sustainability'))
    return <IconSustainabilityDynamic className={classes['icon-details']} />
  if (title?.includes('seafood quality'))
    return <IconSeafoodQualityDynamic className={classes['icon-details']} />
  if (title?.includes('member perks'))
    return <IconMemberPerksDynamic className={classes['icon-details']} />
  if (title?.includes('flexibility'))
    return <IconFlexibilityDynamic className={classes['icon-details']} />
  if (title?.includes('shipping'))
    return <IconCurrentlyShippingDynamic className={classes['icon-details']} />

  // Fallback
  return <IconProductDynamic className={classes['icon-details']} />
}

const ProductDetailsList = ({
  fields,
  enableToggle = false,
  expandOnLoad = false,
  showIcon = false,
  className = ''
}) => {
  // Start collapsed by default unless expandOnLoad is true
  const [height, setHeight] = useState(expandOnLoad ? 'auto' : 0)

  const toggleExpand = () => {
    if (!enableToggle) return
    setHeight(height === 0 ? 'auto' : 0)
  }

  return (
    <div
      className={`${classes['product-details']} ${classes[className]} ${
        enableToggle ? '' : classes['disable-toggle']
      }`}
    >
      <h4 className={classes['product-details__title']} onClick={toggleExpand}>
        {showIcon && getIconForTitle(fields.detailsTitle.toLowerCase())}
        {fields.detailsTitle}

        {height !== 0 ? (
          <IconMinus className={classes['minus']} />
        ) : (
          <IconPlus className={classes['plus']} />
        )}
      </h4>

      {fields.description != null ? (
        <Expand open={height !== 0} duration={300}>
          <div className={`${classes['product-details__description']} body`}>
            {fields.description}
          </div>
        </Expand>
      ) : (
        <Expand open={height !== 0} duration={300}>
          <ul className={classes['product-details__items']}>
            {fields.detailsItems?.map((item, index) => {
              if (item.text) {
                return (
                  <li className={classes['product-details__item']} key={item._key}>
                    <PortableText value={item.text[0]} />
                  </li>
                )
              }
              return (
                <li className={classes['product-details__item']} key={index}>
                  {fields.subtitle && <><p><strong>{fields.subtitle}</strong></p><br /></>}
                  <p>{item}</p><br />
                  {fields.pointsOfInterest?.map((point, index) => {
                    return (
                      <p key={index}>
                        • <strong>{point.title}:</strong> {point.description}
                      </p>
                    )
                  })}
                  {fields.footer && <><br /><p>{fields.footer}</p></>}
                </li>
              )
            })}
          </ul>
        </Expand>
      )}
    </div>
  )
}

export default ProductDetailsList
